import {
    Box,
    Typography,
    Chip,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    InputAdornment,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../Config/AxiosInstance";
import { ApiList } from "../../Config/ApiList";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Loader from "../../Components/Loader";

// Referral usage: which codes get used, who owns them, who they brought in,
// and whether those people ever paid. One call for the code table; each
// row lazily fetches its referees on expand. Premium days come from
// RevenueCat periods server-side (service/premium_days.js) and include
// trial days — the split is shown per referee.

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");
const fmtDays = (n) => (n ? `${Math.round(n)}d` : "—");

const STATUS_STYLES = {
    pending: { bg: "#f5f5f5", fg: "#555" },
    active: { bg: "#e3f2fd", fg: "#0d47a1" },
    qualified: { bg: "#e8f5e9", fg: "#1b5e20" },
    paid: { bg: "#e8f5e9", fg: "#1b5e20" },
    voided: { bg: "#fbe9e7", fg: "#a33c1d" },
    frozen: { bg: "#fbe9e7", fg: "#a33c1d" },
};

const CODE_COLUMNS = [
    { key: "code", label: "Code" },
    { key: "owner_name", label: "Owner" },
    { key: "uses", label: "Uses", numeric: true },
    { key: "ever_premium", label: "Ever premium", numeric: true },
    { key: "currently_premium", label: "Premium now", numeric: true },
    { key: "premium_days", label: "Premium days", numeric: true },
    { key: "last_used_at", label: "Last used", numeric: true },
];

const Stat = ({ label, value }) => (
    <Box sx={{ background: "#fff", borderRadius: "10px", px: 2, py: 1.5, minWidth: "150px", flex: "1 1 150px" }}>
        <Typography sx={{ fontSize: "12px", color: "#666" }}>{label}</Typography>
        <Typography sx={{ fontSize: "24px", fontWeight: 600, color: "#000" }}>{value}</Typography>
    </Box>
);

const PremiumChip = ({ r }) => {
    if (r.currently_premium) return <Chip size="small" label="Premium now" sx={{ background: "#e8f5e9", color: "#1b5e20", fontWeight: 600 }} />;
    if (r.ever_premium) return <Chip size="small" label="Was premium" sx={{ background: "#fff3e0", color: "#8a4b00", fontWeight: 600 }} />;
    return <Chip size="small" label="Never" sx={{ background: "#f5f5f5", color: "#777" }} />;
};

const RefereeTable = ({ codeId }) => {
    const [state, setState] = useState({ loading: true, rows: [], error: null });

    useEffect(() => {
        (async () => {
            try {
                const response = await AxiosInstance.get(ApiList.REFERRAL_CODE_REFEREES(codeId));
                if (response?.data?.s === 1) setState({ loading: false, rows: response.data.r?.referees || [], error: null });
                else setState({ loading: false, rows: [], error: response?.data?.m || "Failed to load referees" });
            } catch (e) {
                console.error("Error fetching referees:", e);
                setState({ loading: false, rows: [], error: "Could not reach the server" });
            }
        })();
    }, [codeId]);

    if (state.loading) return <Typography sx={{ p: 2, fontSize: "13px", color: "#666" }}>Loading referees…</Typography>;
    if (state.error) return <Typography sx={{ p: 2, fontSize: "13px", color: "#a33c1d" }}>{state.error}</Typography>;
    if (!state.rows.length) return <Typography sx={{ p: 2, fontSize: "13px", color: "#666" }}>No referees.</Typography>;

    return (
        <Table size="small" sx={{ background: "#fafafa" }}>
            <TableHead>
                <TableRow>
                    {["User", "Email", "Signed up", "Used code", "Referral", "Premium", "Total", "Paid", "Trial"].map((h, i) => (
                        <TableCell key={h} align={i >= 6 ? "right" : "left"} sx={{ fontWeight: 600, fontSize: "12px" }}>{h}</TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {state.rows.map((r) => {
                    const st = STATUS_STYLES[r.referral_status] || STATUS_STYLES.pending;
                    return (
                        <TableRow key={r.referral_id}>
                            <TableCell>
                                {r.name}
                                {r.deleted && <Chip size="small" label="Deleted" sx={{ ml: 1, height: "18px", fontSize: "10px" }} />}
                            </TableCell>
                            <TableCell>{r.email || "—"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.signup_at)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{fmtDate(r.applied_at)}</TableCell>
                            <TableCell>
                                <Chip size="small" label={r.referral_status} sx={{ background: st.bg, color: st.fg, textTransform: "capitalize" }} />
                            </TableCell>
                            <TableCell><PremiumChip r={r} /></TableCell>
                            <TableCell align="right">{fmtDays(r.total_days)}</TableCell>
                            <TableCell align="right">{fmtDays(r.paid_days)}</TableCell>
                            <TableCell align="right">{fmtDays(r.trial_days)}</TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

const Referrals = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ summary: null, codes: [] });
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [showUnused, setShowUnused] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const [orderBy, setOrderBy] = useState("uses");
    const [order, setOrder] = useState("desc");

    const load = useCallback(async (all) => {
        setLoading(true);
        try {
            const response = await AxiosInstance.get(ApiList.REFERRAL_CODES, { params: all ? { all: 1 } : {} });
            if (response?.data?.s === 1) { setData(response.data.r); setError(null); }
            else setError(response?.data?.m || "Failed to load referral codes");
        } catch (e) {
            console.error("Error fetching referral codes:", e);
            setError("Could not reach the server");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(showUnused); }, [load, showUnused]);

    const handleSort = (key) => {
        if (orderBy === key) setOrder(order === "asc" ? "desc" : "asc");
        else { setOrderBy(key); setOrder("desc"); }
    };

    const visible = useMemo(() => {
        const term = search.trim().toLowerCase();
        const out = (data.codes || []).filter((c) =>
            !term || [c.code, c.owner_name, c.owner_email].some((v) => v && String(v).toLowerCase().includes(term))
        );
        const dir = order === "asc" ? 1 : -1;
        out.sort((a, b) => {
            const av = a[orderBy], bv = b[orderBy];
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            const ad = Date.parse(av), bd = Date.parse(bv);
            if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * dir;
            return String(av).localeCompare(String(bv)) * dir;
        });
        return out;
    }, [data.codes, search, orderBy, order]);

    const s = data.summary || {};
    const pct = s.referrals ? Math.round((s.referees_ever_premium / s.referrals) * 100) : 0;

    return (
        <Box sx={{ width: "100%", height: "100vh", background: "#B9CC0310", overflow: "auto" }}>
            <Box sx={{ width: "100%", p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: { xs: "18px", md: "28px" }, fontWeight: "300", color: "#000" }}>
                    Referrals
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    <TextField
                        size="small"
                        placeholder="Search code or owner"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ background: "#fff", borderRadius: "8px", minWidth: "240px" }}
                        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                    />
                    <Chip
                        label="Show unused codes"
                        onClick={() => setShowUnused(!showUnused)}
                        variant={showUnused ? "filled" : "outlined"}
                        sx={showUnused ? { background: "#B9CC03", color: "#000", fontWeight: 600 } : {}}
                    />
                </Box>
            </Box>

            {s.referrals != null && (
                <Box sx={{ px: 2, pb: 2, display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Stat label="Referrals" value={s.referrals} />
                    <Stat label="Codes used" value={s.codes_used} />
                    <Stat label="Ever premium" value={`${s.referees_ever_premium} (${pct}%)`} />
                    <Stat label="Premium now" value={s.referees_currently_premium} />
                    <Stat label="Total premium days" value={Math.round(s.premium_days || 0)} />
                </Box>
            )}

            <Box sx={{ px: 2, pb: 4 }}>
                {loading ? (
                    <Loader />
                ) : error ? (
                    <Typography sx={{ color: "#a33c1d" }}>{error}</Typography>
                ) : (
                    <TableContainer sx={{ background: "#fff", borderRadius: "10px" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: "40px" }} />
                                    {CODE_COLUMNS.map((c) => (
                                        <TableCell key={c.key} align={c.numeric ? "right" : "left"} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                            <TableSortLabel
                                                active={orderBy === c.key}
                                                direction={orderBy === c.key ? order : "desc"}
                                                onClick={() => handleSort(c.key)}
                                            >
                                                {c.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.length === 0 && (
                                    <TableRow><TableCell colSpan={CODE_COLUMNS.length + 1} sx={{ color: "#666" }}>No codes match.</TableCell></TableRow>
                                )}
                                {visible.map((c) => {
                                    const open = expanded === c.code_id;
                                    return (
                                        <React.Fragment key={c.code_id}>
                                            <TableRow
                                                hover
                                                onClick={() => c.uses > 0 && setExpanded(open ? null : c.code_id)}
                                                sx={{ cursor: c.uses > 0 ? "pointer" : "default", "& > *": { borderBottom: open ? "unset" : undefined } }}
                                            >
                                                <TableCell>
                                                    {c.uses > 0 && (
                                                        <IconButton size="small">{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                                                    {c.code}
                                                    {c.is_vanity && <Chip size="small" label="Vanity" sx={{ ml: 1, height: "18px", fontSize: "10px", background: "#B9CC0340" }} />}
                                                    {c.status === "disabled" && <Chip size="small" label="Disabled" sx={{ ml: 1, height: "18px", fontSize: "10px" }} />}
                                                </TableCell>
                                                <TableCell>{c.owner_name || "— (unattached)"}</TableCell>
                                                <TableCell align="right">{c.uses}</TableCell>
                                                <TableCell align="right">{c.ever_premium}</TableCell>
                                                <TableCell align="right">{c.currently_premium}</TableCell>
                                                <TableCell align="right">{fmtDays(c.premium_days)}</TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{fmtDate(c.last_used_at)}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell sx={{ py: 0 }} colSpan={CODE_COLUMNS.length + 1}>
                                                    <Collapse in={open} timeout="auto" unmountOnExit>
                                                        <Box sx={{ my: 1 }}><RefereeTable codeId={c.code_id} /></Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default Referrals;
